import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import { type EntryV2, useCacheMutations } from "@/shared/cache";
import { useApi } from "@/shared/utils/request";
import { useData } from "@/v2/contexts/DataProvider";
import { type AddPayload, addAction } from "@/v2/hooks/actions/add";
import {
	type CollapseOptions,
	collapseAction,
} from "@/v2/hooks/actions/collapse";
import { deleteAction } from "@/v2/hooks/actions/delete";
import { duplicateAction } from "@/v2/hooks/actions/duplicate";
import {
	type EditPayload,
	editAction,
	toUpdateBody,
} from "@/v2/hooks/actions/edit";
import { formatEntryId } from "@/v2/hooks/actions/entryId";
import {
	type ExportResult,
	executeExport,
	exportAction,
} from "@/v2/hooks/actions/export";
import { type GroupOptions, groupAction } from "@/v2/hooks/actions/group";
import { type MergeOptions, mergeAction } from "@/v2/hooks/actions/merge";
import { resolveFeedbackAction } from "@/v2/hooks/actions/resolveFeedback";
import {
	type SetStatusTarget,
	setStatusAction,
	setStatusManyAction,
} from "@/v2/hooks/actions/setStatus";
import {
	type ActionDeps,
	type ApiError,
	actingHeaders,
	apiCall,
} from "@/v2/hooks/actions/types";
import { ActionQueue, type QueuedAction } from "@/v2/hooks/useActionQueue";

class ExportAfterEditError extends Error {}

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

type ActionsContextType = {
	deleteEntries: (ids: number[], timekeeperId?: string | null) => void;
	editEntry: (entry: EditPayload) => void;
	editThenExport: (entry: EditPayload) => void;
	addEntry: (entry: AddPayload, onSuccess?: () => void) => void;
	duplicateEntries: (ids: number[], timekeeperId?: string | null) => void;
	groupEntries: (
		ids: number[],
		options: GroupOptions,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	mergeEntries: (
		ids: number[],
		options: MergeOptions,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	collapseEntries: (
		ids: number[],
		mainSourceId: number,
		options: CollapseOptions,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	exportEntries: (ids: number[], timekeeperId?: string | null) => void;
	resolveFeedback: (entryId: number) => void;
	/**
	 * Move a single entry between cheap-path states (tracked, reviewed,
	 * deleted) via PATCH /v2/entries/{id}. Export transitions stay on the
	 * existing `/records/exportRecords` flow — use `exportEntries` for those.
	 */
	setStatus: (
		id: number,
		target: SetStatusTarget,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	/**
	 * Fan-out wrapper around `setStatus` — enqueues one PATCH per id. Each
	 * action gets its own toast and rollback, so a partial failure in the
	 * middle of a multi-row mutation surfaces precisely.
	 */
	setStatusMany: (
		ids: number[],
		target: SetStatusTarget,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	enqueue: (action: QueuedAction) => void;
	failedExports: Map<number, string>;
	pendingEntryIds: Set<number>;
	queueSize: number;
};

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ActionQueueProvider({ children }: { children: ReactNode }) {
	const { makeApiRequest } = useApi();
	const { updateEntries, refreshRecords } = useData();
	const { optimisticUpdate, refresh } = useCacheMutations();
	const [queueSize, setQueueSize] = useState(0);
	const [failedExports, setFailedExports] = useState<Map<number, string>>(
		new Map(),
	);
	const [pendingEntryIds, setPendingEntryIds] = useState<Set<number>>(
		new Set(),
	);

	// v2-cache-aware deps. These mirror the legacy DataProvider helpers but
	// target the `entries_v2` slice — actions invoked from any page where the
	// CacheProvider is mounted (which is the whole app) keep both stores in
	// sync. `updateEntriesV2` is shaped the same as the legacy `updateEntries`:
	// take a snapshot, set the next state, return a rollback closure.
	const updateEntriesV2 = useCallback(
		(updater: (prev: EntryV2[]) => EntryV2[]) =>
			optimisticUpdate("entries_v2", updater),
		[optimisticUpdate],
	);
	const refreshRecordsV2 = useCallback(() => refresh("entries_v2"), [refresh]);

	const trackPending = useCallback(
		(ids: number[], action: QueuedAction): QueuedAction => {
			setPendingEntryIds((prev) => {
				const next = new Set(prev);
				for (const id of ids) next.add(id);
				return next;
			});
			const removePending = () =>
				setPendingEntryIds((prev) => {
					const next = new Set(prev);
					for (const id of ids) next.delete(id);
					return next;
				});
			return {
				...action,
				onSuccess: () => {
					removePending();
					action.onSuccess?.();
				},
				onError: () => {
					removePending();
					action.onError?.();
				},
			};
		},
		[],
	);

	// Keep deps in a ref so the queue always uses the latest closures
	const depsRef = useRef<ActionDeps>({
		makeApiRequest,
		updateEntries,
		refreshRecords,
		updateEntriesV2,
		refreshRecordsV2,
	});
	depsRef.current = {
		makeApiRequest,
		updateEntries,
		refreshRecords,
		updateEntriesV2,
		refreshRecordsV2,
	};

	// Stable deps proxy that always reads from the ref
	const stableDeps = useRef<ActionDeps>({
		makeApiRequest: (opts) => depsRef.current.makeApiRequest(opts),
		updateEntries: (updater) => depsRef.current.updateEntries(updater),
		refreshRecords: () => depsRef.current.refreshRecords(),
		updateEntriesV2: (updater) =>
			depsRef.current.updateEntriesV2?.(updater) ?? (() => undefined),
		refreshRecordsV2: () =>
			depsRef.current.refreshRecordsV2?.() ?? Promise.resolve(),
	}).current;

	// Lazily create the queue once
	const queueRef = useRef<ActionQueue | null>(null);
	if (!queueRef.current) {
		queueRef.current = new ActionQueue(setQueueSize);
	}

	const enqueue = useCallback((action: QueuedAction) => {
		queueRef.current?.enqueue(action);
	}, []);

	// --- Export completion handler ---

	const handleExportComplete = useCallback((result: ExportResult) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			// Clear successes
			for (const id of result.exportedIds) next.delete(id);
			// Add failures
			for (const { id, error } of result.failedEntries) next.set(id, error);
			return next;
		});
	}, []);

	const recordExportFailure = useCallback((id: number, error: string) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			next.set(id, error);
			return next;
		});
	}, []);

	// --- Clear failed export when entry is edited or deleted ---

	const clearFailedExports = useCallback((ids: number[]) => {
		setFailedExports((prev) => {
			if (!ids.some((id) => prev.has(id))) return prev;
			const next = new Map(prev);
			for (const id of ids) next.delete(id);
			return next;
		});
	}, []);

	// --- Dispatch helpers ---

	const deleteEntries = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			clearFailedExports(ids);
			enqueue(trackPending(ids, deleteAction(ids, stableDeps, timekeeperId)));
		},
		[enqueue, stableDeps, clearFailedExports, trackPending],
	);

	const editEntry = useCallback(
		(entry: EditPayload) => {
			clearFailedExports([entry.id]);
			enqueue(trackPending([entry.id], editAction(entry, stableDeps)));
		},
		[enqueue, stableDeps, clearFailedExports, trackPending],
	);

	const editThenExport = useCallback(
		(entry: EditPayload) => {
			const id = entry.id;
			clearFailedExports([id]);
			const undoOptimistic = stableDeps.updateEntries((prev) =>
				prev.map((e) => (e.id === id ? { ...e, ...entry } : e)),
			);
			const actionId = crypto.randomUUID();
			let editSucceeded = false;
			enqueue(
				trackPending([id], {
					id: actionId,
					execute: async () => {
						await apiCall(stableDeps, {
							endpoint: `/v2/entries/${formatEntryId(entry.id)}`,
							method: "PATCH",
							body: toUpdateBody(entry),
							headers: actingHeaders(entry.timekeeperId),
						});
						editSucceeded = true;
						try {
							await executeExport(
								actionId,
								[id],
								stableDeps,
								handleExportComplete,
								recordExportFailure,
								entry.timekeeperId,
							);
						} catch (err) {
							throw new ExportAfterEditError(
								err instanceof Error ? err.message : "Export failed",
							);
						}
					},
					rollback: () => {
						if (!editSucceeded) undoOptimistic();
					},
					toast: {
						loading: "Saving and exporting…",
						success: "Entry saved and exported",
						error: (err) =>
							err instanceof ExportAfterEditError
								? "Entry saved but export failed"
								: "Failed to save entry",
					},
					onSuccess: () => stableDeps.refreshRecords(),
					onError: () => stableDeps.refreshRecords(),
				}),
			);
		},
		[
			enqueue,
			stableDeps,
			clearFailedExports,
			handleExportComplete,
			recordExportFailure,
			trackPending,
		],
	);

	const addEntry = useCallback(
		(entry: AddPayload, onSuccess?: () => void) =>
			enqueue(addAction(entry, stableDeps, onSuccess, recordExportFailure)),
		[enqueue, stableDeps, recordExportFailure],
	);

	const duplicateEntries = useCallback(
		(ids: number[], timekeeperId?: string | null) =>
			enqueue(
				trackPending(ids, duplicateAction(ids, stableDeps, timekeeperId)),
			),
		[enqueue, stableDeps, trackPending],
	);

	const groupEntries = useCallback(
		(
			ids: number[],
			options: GroupOptions,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) =>
			enqueue(
				trackPending(
					ids,
					groupAction(ids, options, stableDeps, onApiError, timekeeperId),
				),
			),
		[enqueue, stableDeps, trackPending],
	);

	const mergeEntries = useCallback(
		(
			ids: number[],
			options: MergeOptions,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) =>
			enqueue(
				trackPending(
					ids,
					mergeAction(ids, options, stableDeps, onApiError, timekeeperId),
				),
			),
		[enqueue, stableDeps, trackPending],
	);

	const collapseEntries = useCallback(
		(
			ids: number[],
			mainSourceId: number,
			options: CollapseOptions,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) =>
			enqueue(
				trackPending(
					ids,
					collapseAction(
						ids,
						mainSourceId,
						options,
						stableDeps,
						onApiError,
						timekeeperId,
					),
				),
			),
		[enqueue, stableDeps, trackPending],
	);

	const exportEntries = useCallback(
		(ids: number[], timekeeperId?: string | null) =>
			enqueue(
				trackPending(
					ids,
					exportAction(
						ids,
						stableDeps,
						handleExportComplete,
						recordExportFailure,
						timekeeperId,
					),
				),
			),
		[
			enqueue,
			stableDeps,
			handleExportComplete,
			recordExportFailure,
			trackPending,
		],
	);

	const resolveFeedback = useCallback(
		(entryId: number) =>
			enqueue(
				trackPending([entryId], resolveFeedbackAction(entryId, stableDeps)),
			),
		[enqueue, stableDeps, trackPending],
	);

	const setStatus = useCallback(
		(
			id: number,
			target: SetStatusTarget,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) => {
			clearFailedExports([id]);
			enqueue(
				trackPending(
					[id],
					setStatusAction(id, target, stableDeps, onApiError, timekeeperId),
				),
			);
		},
		[enqueue, stableDeps, clearFailedExports, trackPending],
	);

	const setStatusMany = useCallback(
		(
			ids: number[],
			target: SetStatusTarget,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) => {
			if (ids.length === 0) return;
			clearFailedExports(ids);
			// Single id → use the simple per-row action so the toast wording
			// matches the row-popover case. Two or more → bundle into one queued
			// action with a batch toast (one PATCH per row underneath).
			if (ids.length === 1) {
				enqueue(
					trackPending(
						[ids[0]],
						setStatusAction(
							ids[0],
							target,
							stableDeps,
							onApiError,
							timekeeperId,
						),
					),
				);
				return;
			}
			enqueue(
				trackPending(
					ids,
					setStatusManyAction(
						ids,
						target,
						stableDeps,
						onApiError,
						timekeeperId,
					),
				),
			);
		},
		[enqueue, stableDeps, clearFailedExports, trackPending],
	);

	return (
		<ActionsContext.Provider
			value={{
				deleteEntries,
				editEntry,
				editThenExport,
				addEntry,
				duplicateEntries,
				groupEntries,
				mergeEntries,
				collapseEntries,
				exportEntries,
				resolveFeedback,
				setStatus,
				setStatusMany,
				enqueue,
				failedExports,
				pendingEntryIds,
				queueSize,
			}}
		>
			{children}
		</ActionsContext.Provider>
	);
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export function useActions() {
	const ctx = useContext(ActionsContext);
	if (!ctx)
		throw new Error("useActions must be used within ActionQueueProvider");
	return ctx;
}
